import { useEffect, useState } from 'react'
import { useCommonT } from '../../i18n'
import { Link } from 'react-router-dom'
import {
  readQueuedAdds, readStoppedDeckDrafts, resolveDeckReview, resolveQueueReview, subscribePendingWork, type DeckReviewAction, type ReviewAction,
} from '../../lib/offline/runtime'
import type { DeckDraft } from '../../lib/offline/deckOutbox'
import { ageDays, isAddReceipt, reviewOptions, type OpRecord } from '../../lib/offline/opQueue'
import { HOLD, OP_STATE } from '../../lib/offline/offline.codes'
import { DECK_ACT, DECK_STOP, REVIEW_BTN, REVIEW_OPT } from '../../lib/offline/offlineUi.codes'

function itemCount(r: OpRecord): number {
  try {
    return r.local_refs.length
  } catch {
    return 0
  }
}

/**
 * C4.5 review: queued adds held 25 days or more (Send below 29 days,
 * Discard, Add again as new), and adds whose items failed (Try the failed
 * cards again, Dismiss). Nothing here ever sends on its own.
 */
export default function OfflineReview() {
  const t = useCommonT()
  const [records, setRecords] = useState<OpRecord[]>([])
  const [decks, setDecks] = useState<DeckDraft[]>([])
  useEffect(() => {
    let alive = true
    const load = () => {
      void readQueuedAdds().then(r => { if (alive) setRecords(r) }, () => {})
      void readStoppedDeckDrafts().then(d => { if (alive) setDecks(d) }, () => {})
    }
    load()
    const off = subscribePendingWork(load)
    return () => { alive = false; off() }
  }, [])
  const now = Date.now()
  const old = records.filter(r => r.hold?.reason === HOLD.needsReview)
  const failed = records.filter(r => r.state === OP_STATE.done && isAddReceipt(r.receipt) && r.receipt.errors.length > 0)
  const hard = records.filter(r => r.state === OP_STATE.failedPermanent)
  if (old.length + failed.length + hard.length + decks.length === 0) return null
  const act = (r: OpRecord, a: ReviewAction) => { void resolveQueueReview(r.op_id, a) }
  const actDeck = (d: DeckDraft, a: DeckReviewAction) => { void resolveDeckReview(d.deck_id, a) }
  const btn = REVIEW_BTN
  return (
    <div data-testid="offline-review" className="space-y-2 pt-2 border-t border-white/5">
      <h3 className="text-sm font-semibold text-gray-200">{t('offline.review.title')}</h3>
      {old.map(r => {
        const opts = reviewOptions(r, now)
        return (
          <div key={r.op_id} className="text-xs text-gray-300 space-y-1">
            <p>{t('offline.review.old_add', { count: itemCount(r), days: ageDays(r, now) })}</p>
            {opts.includes(REVIEW_OPT.addAgain) && r.attempts > 0 && <p className="text-amber-300">{t('offline.review.add_again_warning')}</p>}
            <div className="flex gap-2">
              {opts.includes(REVIEW_OPT.send) && <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.send)}>{t('offline.review.send')}</button>}
              {opts.includes(REVIEW_OPT.addAgain) && <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.addAgain)}>{t('offline.review.add_again')}</button>}
              <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.discard)}>{t('offline.review.discard')}</button>
            </div>
          </div>
        )
      })}
      {failed.map(r => (
        <div key={r.op_id} className="text-xs text-gray-300 space-y-1">
          <p>{t('offline.review.failed', { count: isAddReceipt(r.receipt) ? r.receipt.errors.length : 0 })}</p>
          <div className="flex gap-2">
            <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.retryFailed)}>{t('offline.review.retry_failed')}</button>
            <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.discard)}>{t('offline.review.dismiss')}</button>
          </div>
        </div>
      ))}
      {decks.map(d => (
        <div key={d.deck_id} data-testid="offline-review-deck" data-status={d.status ?? ''} className="text-xs text-gray-300 space-y-1">
          <p>{d.status === DECK_STOP.conflict ? t('offline.review.deck_conflict') : d.status === DECK_STOP.missing ? t('offline.review.deck_missing') : t('offline.review.deck_failed')}</p>
          <div className="flex gap-2 flex-wrap">
            {d.status !== DECK_STOP.missing && <Link className={btn} to={`/decks/${encodeURIComponent(d.deck_id)}`}>{t('offline.review.open_deck')}</Link>}
            {d.status === DECK_STOP.conflict && <button type="button" className={btn} onClick={() => actDeck(d, DECK_ACT.keepMine)}>{t('offline.review.keep_mine')}</button>}
            {d.status === DECK_STOP.failed && <button type="button" className={btn} onClick={() => actDeck(d, DECK_ACT.tryAgain)}>{t('offline.review.try_again')}</button>}
            <button type="button" className={btn} onClick={() => actDeck(d, DECK_ACT.discard)}>{t('offline.review.discard')}</button>
          </div>
        </div>
      ))}
      {hard.map(r => (
        <div key={r.op_id} className="text-xs text-gray-300 space-y-1">
          <p>{t('offline.review.failed', { count: itemCount(r) })}</p>
          <div className="flex gap-2">
            <button type="button" className={btn} onClick={() => act(r, REVIEW_OPT.discard)}>{t('offline.review.dismiss')}</button>
          </div>
        </div>
      ))}
    </div>
  )
}
